import { useSearchParams } from 'react-router';
import { useMemo } from 'react';

export const useQueryParams = () => {
  const [searchParams, setSearchParams] = useSearchParams();

  const queryParams = useMemo(() => {
    const params = {};
    for (const [key, value] of searchParams.entries()) {
      params[key] = value;
    }
    return params;
  }, [searchParams]);

  const setQueryParam = (key, value) => {
    const newParams = new URLSearchParams(searchParams);
    if (value === null || value === undefined || value === '') {
      newParams.delete(key);
    } else {
      newParams.set(key, value);
    }
    setSearchParams(newParams);
  };

  const setMultipleQueryParams = (params) => {
    const newParams = new URLSearchParams(searchParams);
    Object.entries(params).forEach(([key, value]) => {
      if (value === null || value === undefined || value === '') {
        newParams.delete(key);
      } else {
        newParams.set(key, value);
      }
    });
    setSearchParams(newParams);
  };

  // Enhanced helpers for common use cases
  const setPagination = (page, limit) => {
    setMultipleQueryParams({ page: page?.toString(), limit: limit?.toString() });
  };

  const setSorting = (sortBy, sortOrder) => {
    setMultipleQueryParams({ sortBy, sortOrder });
  };

  const setFilters = (filters) => {
    const filterParams = {};
    Object.entries(filters).forEach(([key, value]) => {
      if (Array.isArray(value)) {
        filterParams[key] = value.join(',');
      } else {
        filterParams[key] = value;
      }
    });
    setMultipleQueryParams(filterParams);
  };

  const clearAllParams = () => {
    setSearchParams(new URLSearchParams());
  };

  // Parse helpers
  const getPagination = () => ({
    page: parseInt(queryParams.page) || 1,
    limit: parseInt(queryParams.limit) || 10
  });

  const getSorting = () => ({
    sortBy: queryParams.sortBy || '',
    sortOrder: queryParams.sortOrder || 'asc'
  });

  const getArrayParam = (key) => {
    const value = queryParams[key];
    return value ? value.split(',') : [];
  };

  return {
    queryParams,
    setQueryParam,
    setMultipleQueryParams,
    setPagination,
    setSorting,
    setFilters,
    clearAllParams,
    getPagination,
    getSorting,
    getArrayParam,
    searchParams
  };
};

export default useQueryParams;